import React from 'react';
import './quiz-footer.css';

/**
 * Shared bottom action-bar shell for every Blast screen's "advance to the
 * next step" footer (config, range, view range, answer, results). This is a
 * single source of truth for the positioning math that kept getting
 * hand-rolled — and re-broken — separately on each screen:
 *
 *   - position: absolute (never fixed). This app renders inside
 *     ScaleWrapper, which scales the whole 375x812 canvas with CSS `zoom`.
 *     `position: fixed` descendants anchor to the real, unzoomed browser
 *     viewport instead of the canvas, detaching visibly from their intended
 *     position whenever the zoom scale isn't exactly 1:1 (i.e. on almost
 *     every real device). `position: absolute`, anchored to the nearest
 *     positioned ancestor (each screen's own container), stays correctly
 *     attached to the canvas at any scale.
 *   - width is NOT hardcoded. BlastConfigScreen's footer is the reference
 *     for what every screen's footer should look like: its positioned
 *     ancestor (.blast-screen-content) is a full 375px box, so plain
 *     left:0/right:0 with 16px of padding gives a 343px-wide button, 16px
 *     inset from the true screen edge on each side. BlastRangeScreen,
 *     BlastViewRangeScreen, and BlastResultsScreen all anchor to an
 *     equally-375px-wide ancestor, so they match Config automatically with
 *     the same left:0/right:0 + 16px padding — no extra work needed.
 *     BlastAnswerScreen is the one exception: its ancestor
 *     (.answer-container) is an explicit 327px box, 24px narrower than the
 *     canvas on each side (it's centered there on purpose, for its cards/
 *     circles/etc — not something to change just for the footer). Left
 *     alone, its footer would end up 343px narrower than Config's, not
 *     343px wide. leftOffset/rightOffset exist for exactly this: passing
 *     -8 on each bleeds the footer 8px past .answer-container's own edges,
 *     landing it at the same 16px-from-true-edge, 343px-wide result as
 *     every other screen, without touching .answer-container itself (and
 *     therefore without moving anything else on that screen).
 *
 * @param {React.ReactNode} [props.topContent] - Rendered above the button
 *   row(s), inside the footer (e.g. BlastAnswerScreen's scenario note).
 * @param {React.ReactNode} props.children - One or more <QuizFooterRow>s
 *   (or any full-width block, e.g. BlastResultsScreen's Dashboard button).
 * @param {number} [props.horizontalPadding=16] - Left/right padding in px,
 *   inset from this box's own (possibly offset) edges.
 * @param {number} [props.leftOffset=0] / [props.rightOffset=0] - px to bleed
 *   past the positioned ancestor's own edge (negative = bleed outward).
 *   Only needed when that ancestor is narrower than the 375px canvas.
 * @param {number} [props.bottomFallback=8] - Fallback px for
 *   Flat px value, not env(safe-area-inset-bottom) — the real physical
 *   inset (~34px on notched iPhones) turned out to be TOO MUCH once
 *   applied (buttons sat with excess gap below), while the original
 *   flat fallback numbers (8/16px) were TOO LITTLE (buttons crowded the
 *   true screen edge, confirmed on a real iPhone 12). 24px flat is the
 *   correct middle value, confirmed 2026-08-28 — default is 24 now.
 * @param {number} [props.rowGap=10] - Gap between rows (or between
 *   topContent and the first row) in px.
 * @param {string} [props.background='transparent'] - Background color;
 *   BlastResultsScreen uses an opaque page-background color here, everyone
 *   else is transparent (floats over the scroll content).
 * @param {string} [props.className] - Extra class for one-off overrides.
 */
export const QuizFooter = ({
  topContent = null,
  children,
  horizontalPadding = 16,
  leftOffset = 0,
  rightOffset = 0,
  bottomFallback = 24,
  rowGap = 10,
  background = 'transparent',
  className = '',
}) => {
  return (
    <div
      className={`quiz-footer ${className}`.trim()}
      style={{
        left: leftOffset || undefined,
        right: rightOffset || undefined,
        padding: `0 ${horizontalPadding}px ${bottomFallback}px`,
        gap: `${rowGap}px`,
        backgroundColor: background,
      }}
    >
      {topContent}
      {children}
    </div>
  );
};

/**
 * A single row of buttons within a <QuizFooter>. Every direct child is
 * forced to flex: 1 (equal-width split) regardless of the button's own
 * classes — this matches every existing screen's behavior today (each of
 * them already gets an equal split via `.button-large`'s own `flex: 1`, or
 * `.results-btn`'s own `flex: 1`), and guarantees it even for future button
 * types that forget to declare it themselves.
 *
 * @param {number} [props.gap=10] - Gap between buttons in this row, in px.
 */
export const QuizFooterRow = ({ children, gap = 10, className = '' }) => (
  <div className={`quiz-footer-row ${className}`.trim()} style={{ gap: `${gap}px` }}>
    {children}
  </div>
);

export default QuizFooter;
